import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Toast from '../../Shared/Toast';
import HoldButton from '../../Shared/HoldButton';
import { useMercenary } from '../../../contexts/MercenaryContext';
import styles from './CityProjects.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────
interface BuildingStatus {
  buildingType: 'auctionHouse' | 'trainingGround' | 'library' | 'orangeCart' | 'glacierVault' | 'runeForge';
  currentLevel: number;
  maxLevel: number;
  nextLevelConfig: {
    silverCost: number;
    roundsCost: number;
    materialCosts: Record<string, number>;
    benefitDescription: string;
    cooldownHours: number;
    spawnChance: number;
  } | null;
  currentProgress: {
    silver: number;
    rounds: number;
    materials: Record<string, number>;
  };
}

interface Contribution {
  id: number;
  buildingType: string;
  targetLevel: number;
  silverContributed: number;
  roundsContributed: number;
  materialsContributed: Record<string, number>;
  createdAt: string;
  Mercenary?: { name: string };
}

interface ClaimStatus {
  trainingGround: { canClaim: boolean; claimedToday: boolean; xpReward: number; buildingLevel: number };
  library: { canClaim: boolean; nextAvailableAt: string | null; skillPointsReward: number; cooldownHours: number; buildingLevel: number };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const BUILDING_INFO: Record<string, { label: string; description: string }> = {
  auctionHouse: {
    label: 'Auction House',
    description: 'No sanctioned player trading can take place before this is built..'
  },
  trainingGround: {
    label: 'Training Ground',
    description: 'A public training ground where all mercenaries can practice daily for bonus XP.'
  },
  library: {
    label: 'City Library',
    description: 'A grand library granting secondary skill points on each visit.'
  },
  orangeCart: {
    label: "Orange's Traveling Cart",
    description: "Orange roams far beyond the fjords, bartering with distant lands and forgotten tribes. With your support, she will return to Njordheim more often, bringing exotic wares and rare curiosities gathered along her journeys."
  },
  glacierVault: {
    label: 'Glacier Vault',
    description: 'A vault carved deep into the Glacier Cliffs, kept frozen by ancient runes. Once built, mercenaries can store silver and valuables inside - safe from loss, though access is limited each day.'
  },
  runeForge: {
    label: 'Rune Forge',
    description: 'A forge imbued with ancient power where rune carvers can craft magical runes. Requires Ancient Runic Stones contributed by the community.'
  }
};

function progressPercent(contributed: number, total: number): number {
  if (total <= 0) return 100;
  return Math.min(100, Math.round((contributed / total) * 100));
}

function formatCooldown(hours: number): string {
  if (hours <= 0) return 'Once per day';
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function formatTimeLeft(nextAvailableAt: string): string {
  const diff = new Date(nextAvailableAt).getTime() - Date.now();
  if (diff <= 0) return 'Available now';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── Component ────────────────────────────────────────────────────────────────
const CityProjects: React.FC = () => {
  const { mercenary } = useMercenary();
  const [buildings, setBuildings] = useState<BuildingStatus[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [claimStatus, setClaimStatus] = useState<ClaimStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [activeTab, setActiveTab] = useState<'buildings' | 'projects'>('buildings');

  // Contribution form state
  const [contributingTo, setContributingTo] = useState<string | null>(null);
  const [silverInput, setSilverInput] = useState('');
  const [roundsInput, setRoundsInput] = useState('');
  const [materialInputs, setMaterialInputs] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, contribRes, tgRes, libRes] = await Promise.all([
        axios.get('/api/city-buildings/status', { withCredentials: true }),
        axios.get('/api/city-buildings/contributions', { withCredentials: true }),
        axios.get('/api/city-buildings/training-ground/status', { withCredentials: true }),
        axios.get('/api/city-buildings/library/status', { withCredentials: true })
      ]);
      setBuildings(statusRes.data);
      setContributions(contribRes.data);
      setClaimStatus({
        trainingGround: tgRes.data,
        library: libRes.data
      });
    } catch {
      displayToast('Failed to load City Projects data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Contribute ──────────────────────────────────────────────────────────────
  const openContributeForm = (buildingType: string, building: BuildingStatus) => {
    setContributingTo(buildingType);
    setSilverInput('');
    setRoundsInput('');
    if (building.nextLevelConfig) {
      const mats: Record<string, string> = {};
      Object.keys(building.nextLevelConfig.materialCosts).forEach(m => { mats[m] = ''; });
      setMaterialInputs(mats);
    } else {
      setMaterialInputs({});
    }
  };

  const handleContribute = async (buildingType: string) => {
    setSubmitting(true);
    try {
      const materials: Record<string, number> = {};
      Object.entries(materialInputs).forEach(([k, v]) => {
        const n = parseInt(v, 10);
        if (!isNaN(n) && n > 0) materials[k] = n;
      });

      const res = await axios.post('/api/city-buildings/contribute', {
        buildingType,
        silver: parseInt(silverInput, 10) || 0,
        rounds: parseInt(roundsInput, 10) || 0,
        materials
      }, { withCredentials: true });

      displayToast(
        res.data.leveledUp
          ? `${BUILDING_INFO[buildingType].label} leveled up to level ${res.data.newLevel}!`
          : 'Contribution recorded!',
        'success'
      );
      setContributingTo(null);
      fetchAll();
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to contribute', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Claim Training Ground XP ────────────────────────────────────────────────
  const claimTrainingGroundXP = async () => {
    try {
      const res = await axios.post('/api/city-buildings/training-ground/claim', {}, { withCredentials: true });
      displayToast(res.data.message, 'success');
      fetchAll();
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to claim XP', 'error');
    }
  };

  // ── Claim Library Skill Points ───────────────────────────────────────────────
  const claimLibrarySkillPoints = async () => {
    try {
      const res = await axios.post('/api/city-buildings/library/claim', {}, { withCredentials: true });
      displayToast(res.data.message, 'success');
      fetchAll();
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to claim skill points', 'error');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className={styles.loading}>Loading City Buildings...</div>;
  }

  const builtBuildings = buildings.filter(b => b.currentLevel >= 1);
  const projectBuildings = buildings.filter(b => b.currentLevel < b.maxLevel);

  const renderSpecialActions = (building: BuildingStatus) => (
    <>
      {building.buildingType === 'trainingGround' && claimStatus && (
        <div className={styles.specialAction}>
          {claimStatus.trainingGround.claimedToday ? (
            <span className={styles.alreadyClaimed}>XP claimed today (+{claimStatus.trainingGround.xpReward} XP)</span>
          ) : (
            <button className={styles.claimBtn} onClick={claimTrainingGroundXP}>
              Claim Daily XP (+{claimStatus.trainingGround.xpReward} XP, -50 rounds)
            </button>
          )}
        </div>
      )}
      {building.buildingType === 'library' && claimStatus && (
        <div className={styles.specialAction}>
          {claimStatus.library.canClaim ? (
            <button className={styles.claimBtn} onClick={claimLibrarySkillPoints}>
              Claim Skill Point (+{claimStatus.library.skillPointsReward})
            </button>
          ) : (
            <span className={styles.cooldown}>
              Next claim in {formatTimeLeft(claimStatus.library.nextAvailableAt!)}
              &nbsp;(cooldown: {formatCooldown(claimStatus.library.cooldownHours)})
            </span>
          )}
        </div>
      )}
      {building.buildingType === 'auctionHouse' && (
        <div className={styles.specialAction}>
          <span className={styles.builtBadge}>Auction House is open</span>
        </div>
      )}
      {building.buildingType === 'orangeCart' && (
        <div className={styles.specialAction}>
          <span className={styles.builtBadge}>
            {building.nextLevelConfig ? `${building.nextLevelConfig.spawnChance}%` : ''} Daily chance Orange visits the market (14:00–22:00)
          </span>
        </div>
      )}
      {building.buildingType === 'glacierVault' && (
        <div className={styles.specialAction}>
          <span className={styles.builtBadge}>Glacier Vault is open</span>
        </div>
      )}
      {building.buildingType === 'runeForge' && (
        <div className={styles.specialAction}>
          <span className={styles.builtBadge}>Rune Forge is active</span>
        </div>
      )}
    </>
  );

  return (
    <div className={styles.container}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage(null)} />
      )}

      <div className={styles.header}>
        <h1 className={styles.title}>City Buildings</h1>
        <p className={styles.subtitle}>
          Contribute resources to build and upgrade the city's communal structures.<br />
          All players share the benefits of every completed building.
        </p>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tabBtn} ${activeTab === 'buildings' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('buildings')}
        >
          City Buildings
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'projects' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          City Projects
        </button>
      </div>

      {/* ── City Buildings tab ──────────────────────────────────────────────── */}
      {activeTab === 'buildings' && (
        <div className={styles.buildingsGrid}>
          {builtBuildings.length === 0 ? (
            <p className={styles.emptyLog}>No buildings have been constructed yet.</p>
          ) : (
            <>
              {/* Interactive buildings first: Training Ground & Library */}
              {(['trainingGround', 'library'] as const)
                .map(bt => builtBuildings.find(b => b.buildingType === bt))
                .filter((b): b is BuildingStatus => !!b)
                .map(building => {
                  const info = BUILDING_INFO[building.buildingType];
                  return (
                    <div key={building.buildingType} className={styles.buildingCard}>
                      <div className={styles.cardHeader}>
                        <div className={styles.buildingTitleGroup}>
                          <h2 className={styles.buildingName}>{info.label}</h2>
                          <span className={styles.levelBadge}>Level {building.currentLevel} / {building.maxLevel}</span>
                        </div>
                      </div>
                      <p className={styles.buildingDesc}>{info.description}</p>
                      {renderSpecialActions(building)}
                    </div>
                  );
                })}

              {/* Divider + non-interactive built buildings */}
              {builtBuildings.filter(b => !['trainingGround', 'library'].includes(b.buildingType)).length > 0 && (
                <>
                  <div className={styles.sectionDivider}>
                    <span className={styles.dividerLabel}>Built</span>
                  </div>
                  {builtBuildings
                    .filter(b => !['trainingGround', 'library'].includes(b.buildingType))
                    .map(building => {
                      const info = BUILDING_INFO[building.buildingType];
                      return (
                        <div key={building.buildingType} className={`${styles.buildingCard} ${styles.maxed}`}>
                          <div className={styles.cardHeader}>
                            <div className={styles.buildingTitleGroup}>
                              <h2 className={styles.buildingName}>{info.label}</h2>
                              <span className={styles.levelBadge}>Level {building.currentLevel} / {building.maxLevel}</span>
                            </div>
                          </div>
                          <p className={styles.buildingDesc}>{info.description}</p>
                          {renderSpecialActions(building)}
                          <div className={styles.maxedBanner}>
                            {building.maxLevel === 1 || building.currentLevel === building.maxLevel
                              ? 'Fully Built'
                              : `Level ${building.currentLevel} / ${building.maxLevel}`}
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── City Projects tab ───────────────────────────────────────────────── */}
      {activeTab === 'projects' && (
        <>
          <div className={styles.buildingsGrid}>
            {projectBuildings.length === 0 ? (
              <p className={styles.emptyLog}>All buildings are fully constructed!</p>
            ) : projectBuildings.map(building => {
              const info = BUILDING_INFO[building.buildingType];
              const notBuilt = building.currentLevel === 0;
              const cfg = building.nextLevelConfig;
              const silverPct = cfg ? progressPercent(building.currentProgress.silver, cfg.silverCost) : 100;
              const roundsPct = cfg ? progressPercent(building.currentProgress.rounds, cfg.roundsCost) : 100;
              const isContributing = contributingTo === building.buildingType;

              return (
                <div key={building.buildingType} className={styles.buildingCard}>
                  <div className={styles.cardHeader}>
                    <div className={styles.buildingTitleGroup}>
                      <h2 className={styles.buildingName}>{info.label}</h2>
                      <span className={styles.levelBadge}>
                        {notBuilt ? 'Not Built' : `Level ${building.currentLevel} / ${building.maxLevel}`}
                      </span>
                    </div>
                  </div>

                  <p className={styles.buildingDesc}>{info.description}</p>

                  {/* Show hint if not yet built */}
                  {building.buildingType === 'trainingGround' && notBuilt && claimStatus && (
                    <div className={styles.specialAction}>
                      <span className={styles.notBuiltHint}>Build this to claim daily XP</span>
                    </div>
                  )}
                  {building.buildingType === 'library' && notBuilt && claimStatus && (
                    <div className={styles.specialAction}>
                      <span className={styles.notBuiltHint}>Build this to claim skill points</span>
                    </div>
                  )}
                  {building.buildingType === 'glacierVault' && notBuilt && (
                    <div className={styles.specialAction}>
                      <span className={styles.notBuiltHint}>Build this to unlock silver and item storage</span>
                    </div>
                  )}

                  {cfg && (
                    <div className={styles.upgradeSection}>
                      <h4 className={styles.upgradeTitle}>
                        {notBuilt ? 'Construction Cost' : `Upgrade to Level ${building.currentLevel + 1}`}
                      </h4>

                      <div className={styles.progressBlock}>
                        <div className={styles.progressRow}>
                          <span className={styles.progressLabel}>Silver</span>
                          <div className={styles.progressBar}>
                            <div className={styles.progressFill} style={{ width: `${silverPct}%` }} />
                          </div>
                          <span className={styles.progressText}>
                            {building.currentProgress.silver.toLocaleString()} / {cfg.silverCost.toLocaleString()}
                          </span>
                        </div>

                        <div className={styles.progressRow}>
                          <span className={styles.progressLabel}>Rounds</span>
                          <div className={styles.progressBar}>
                            <div className={styles.progressFill} style={{ width: `${roundsPct}%` }} />
                          </div>
                          <span className={styles.progressText}>
                            {building.currentProgress.rounds} / {cfg.roundsCost}
                          </span>
                        </div>

                        {Object.entries(cfg.materialCosts).map(([mat, needed]) => {
                          const have = building.currentProgress.materials[mat] || 0;
                          const pct = progressPercent(have, needed);
                          return (
                            <div key={mat} className={styles.progressRow}>
                              <span className={styles.progressLabel}>{mat}</span>
                              <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                              </div>
                              <span className={styles.progressText}>{have} / {needed}</span>
                            </div>
                          );
                        })}
                      </div>

                      {cfg.benefitDescription && (
                        <p className={styles.benefitHint}>{cfg.benefitDescription}</p>
                      )}

                      {!isContributing ? (
                        <button
                          className={styles.contributeBtn}
                          onClick={() => openContributeForm(building.buildingType, building)}
                        >
                          Contribute
                        </button>
                      ) : (
                        <div className={styles.contributeForm}>
                          {(() => {
                            const silverAvail = mercenary?.silver ?? 0;
                            const roundsAvail = mercenary?.rounds ?? 0;
                            const silverRemaining = Math.max(0, cfg.silverCost - building.currentProgress.silver);
                            const roundsRemaining = Math.max(0, cfg.roundsCost - building.currentProgress.rounds);
                            const silverCap = Math.min(silverAvail, silverRemaining);
                            const roundsCap = Math.min(roundsAvail, roundsRemaining);
                            return (
                              <>
                                <div className={styles.formRow}>
                                  <label>Silver<span className={styles.availHint}>{silverAvail} avail</span></label>
                                  <div className={styles.qtyControl}>
                                    <HoldButton className={styles.qtyBtn} onClick={() => setSilverInput(v => String(Math.max(0, (parseInt(v,10)||0) - 1)))}>−</HoldButton>
                                    <input
                                      type="number" min={0} max={silverCap}
                                      placeholder="0"
                                      className={styles.qtyInput}
                                      value={silverInput}
                                      onChange={e => { const n = e.target.value === '' ? '' : String(Math.max(0, Math.min(silverCap, parseInt(e.target.value,10)||0))); setSilverInput(n); }}
                                    />
                                    <HoldButton className={styles.qtyBtn} onClick={() => setSilverInput(v => String(Math.min(silverCap, (parseInt(v,10)||0) + 1)))}>+</HoldButton>
                                  </div>
                                </div>
                                <div className={styles.formRow}>
                                  <label>Rounds<span className={styles.availHint}>{roundsAvail} avail</span></label>
                                  <div className={styles.qtyControl}>
                                    <HoldButton className={styles.qtyBtn} onClick={() => setRoundsInput(v => String(Math.max(0, (parseInt(v,10)||0) - 1)))}>−</HoldButton>
                                    <input
                                      type="number" min={0} max={roundsCap}
                                      placeholder="0"
                                      className={styles.qtyInput}
                                      value={roundsInput}
                                      onChange={e => { const n = e.target.value === '' ? '' : String(Math.max(0, Math.min(roundsCap, parseInt(e.target.value,10)||0))); setRoundsInput(n); }}
                                    />
                                    <HoldButton className={styles.qtyBtn} onClick={() => setRoundsInput(v => String(Math.min(roundsCap, (parseInt(v,10)||0) + 1)))}>+</HoldButton>
                                  </div>
                                </div>
                                {Object.entries(cfg.materialCosts).map(([mat, needed]) => {
                                  const haveInProgress = building.currentProgress.materials[mat] || 0;
                                  const remaining = Math.max(0, needed - haveInProgress);
                                  const matItem = mercenary?.Inventory?.find((inv: any) => inv.Item?.name === mat);
                                  const matAvail = matItem?.quantity ?? 0;
                                  const matCap = Math.min(matAvail, remaining);
                                  return (
                                    <div key={mat} className={styles.formRow}>
                                      <label>{mat}<span className={styles.availHint}>{matAvail} avail</span></label>
                                      <div className={styles.qtyControl}>
                                        <HoldButton className={styles.qtyBtn} onClick={() => setMaterialInputs(prev => ({ ...prev, [mat]: String(Math.max(0, (parseInt(prev[mat]||'0',10)||0) - 1)) }))}>−</HoldButton>
                                        <input
                                          type="number" min={0} max={matCap}
                                          placeholder="0"
                                          className={styles.qtyInput}
                                          value={materialInputs[mat] || ''}
                                          onChange={e => { const n = e.target.value === '' ? '' : String(Math.max(0, Math.min(matCap, parseInt(e.target.value,10)||0))); setMaterialInputs(prev => ({ ...prev, [mat]: n })); }}
                                        />
                                        <HoldButton className={styles.qtyBtn} onClick={() => setMaterialInputs(prev => ({ ...prev, [mat]: String(Math.min(matCap, (parseInt(prev[mat]||'0',10)||0) + 1)) }))}>+</HoldButton>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                          <div className={styles.formActions}>
                            <button
                              className={styles.contributeSubmitBtn}
                              onClick={() => handleContribute(building.buildingType)}
                              disabled={submitting}
                            >
                              {submitting ? 'Contributing...' : 'Confirm Contribution'}
                            </button>
                            <button
                              className={styles.cancelBtn}
                              onClick={() => setContributingTo(null)}
                              disabled={submitting}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contribution Log */}
          <div className={styles.logSection}>
            <h2 className={styles.logTitle}>Recent Contributions</h2>
            <div className={styles.logScroll}>
              {contributions.length === 0 ? (
                <p className={styles.emptyLog}>No contributions yet.<br />Be the first!</p>
              ) : (
                contributions.map(c => {
                  const parts: string[] = [];
                  if (c.silverContributed > 0) parts.push(`${c.silverContributed.toLocaleString()} silver`);
                  if (c.roundsContributed > 0) parts.push(`${c.roundsContributed} rounds`);
                  Object.entries(c.materialsContributed || {}).forEach(([mat, qty]) => {
                    if (qty > 0) parts.push(`${qty}x ${mat}`);
                  });
                  const buildingLabel = BUILDING_INFO[c.buildingType]?.label || c.buildingType;
                  const mercName = c.Mercenary?.name || 'Unknown';
                  const date = new Date(c.createdAt).toLocaleDateString();
                  return (
                    <div key={c.id} className={styles.logEntry}>
                      <span className={styles.logName}>{mercName}</span>
                      <span className={styles.logText}>
                        contributed {parts.join(', ')} to <strong>{buildingLabel}</strong>
                        {' '}(→ Lvl {c.targetLevel})
                      </span>
                      <span className={styles.logDate}>{date}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CityProjects;
